import {
    ASSET_REF_FULL_RE,
    ASSET_REF_RE,
    parseAssetRefPath,
    resolveDocumentAssetPath,
    type ImportDocumentSnapshot,
} from "@magic-md/core";
import { t as translate } from "@magic-md/i18n";
import { useT } from "@magic-md/i18n/react";
import {
    Check,
    Copy,
    File,
    FileAudio,
    FileArchive,
    FileCode2,
    FileImage,
    FileJson2,
    FileSpreadsheet,
    FileText,
    FileVideo,
    Globe2,
    Hash,
    Link,
    XCircle,
    type LucideIcon,
} from "lucide-react";
import {
    isValidElement,
    useEffect,
    useMemo,
    useRef,
    useState,
    type AnchorHTMLAttributes,
    type ComponentPropsWithoutRef,
    type CSSProperties,
    type HTMLAttributes,
    type ImgHTMLAttributes,
    type MouseEvent,
    type ReactNode,
} from "react";
import { defaultUrlTransform, type Components } from "react-markdown";
import { getFileKind } from "../../lib/file-type";
import { dirNameFromPath } from "../../lib/path-utils";
import { getPlatformService } from "../../platform";

interface MdastNode {
    type?: string;
    value?: string;
    url?: string;
    title?: string | null;
    children?: MdastNode[];
    data?: {
        hProperties?: Record<string, unknown>;
    };
}

interface MarkdownInteractionOptions {
    openFile?: (filePath: string, target?: MarkdownLinkOpenTarget) => Promise<void> | void;
    openExternal?: (url: string) => Promise<void> | void;
}

type MarkdownLinkKind =
    | "anchor"
    | "web"
    | "asset"
    | "markdown"
    | "json"
    | "html"
    | "image"
    | "pdf"
    | "audio"
    | "video"
    | "csv"
    | "code"
    | "file";

type MarkdownLinkSource = "anchor" | "external" | "local" | "asset" | "unknown";

export interface MarkdownLinkTarget {
    kind: MarkdownLinkKind;
    source: MarkdownLinkSource;
    localPath: string | null;
    externalUrl: string | null;
    fragment: MarkdownLinkFragment | null;
}

export interface MarkdownLinkFragment {
    kind: "heading" | "line";
    raw: string;
    heading: string | null;
    line: number | null;
}

export interface MarkdownLinkOpenTarget {
    fragment: MarkdownLinkFragment | null;
    sourceUrl: string | undefined;
}

const URI_SCHEME_RE = /^[a-zA-Z][a-zA-Z\d+.-]*:/;
const WINDOWS_DRIVE_RE = /^[A-Za-z]:[\\/]/;

function stripUrlSuffix(value: string): string {
    const marker = value.search(/[?#]/);
    return marker >= 0 ? value.slice(0, marker) : value;
}

function getUrlSuffix(value: string): string {
    const marker = value.search(/[?#]/);
    return marker >= 0 ? value.slice(marker) : "";
}

function extractUrlFragment(value: string): string | null {
    const hashIndex = value.indexOf("#");
    if (hashIndex < 0) return null;
    const raw = value.slice(hashIndex + 1);
    return raw ? decodeUrlPath(raw) : null;
}

function decodeUrlPath(value: string): string {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

function normalizeSlashPath(value: string): string {
    const isWindowsDrive = WINDOWS_DRIVE_RE.test(value);
    const normalized = value.replace(/\\/g, "/");
    const parts: string[] = [];
    for (const segment of normalized.split("/")) {
        if (!segment || segment === ".") continue;
        if (segment === "..") {
            if (parts.length > 0 && parts[parts.length - 1] !== "..") {
                parts.pop();
                continue;
            }
            if (!normalized.startsWith("/") && !isWindowsDrive) {
                parts.push(segment);
            }
            continue;
        }
        parts.push(segment);
    }

    if (isWindowsDrive) {
        const drive = normalized.slice(0, 2);
        const rest = parts.slice(parts[0] === drive ? 1 : 0).join("/");
        return rest ? `${drive}/${rest}` : `${drive}/`;
    }

    return `${normalized.startsWith("/") ? "/" : ""}${parts.join("/")}`;
}

function isAbsoluteLocalPath(value: string): boolean {
    return value.startsWith("/") || WINDOWS_DRIVE_RE.test(value) || value.startsWith("\\\\");
}

function fileUrlToPath(value: string): string | null {
    try {
        const url = new URL(value);
        if (url.protocol !== "file:") return null;
        const pathname = decodeUrlPath(url.pathname);
        return pathname.match(/^\/[A-Za-z]:\//)
            ? pathname.slice(1)
            : pathname;
    } catch {
        return null;
    }
}

function resolveAssetRefDocumentPath(
    documentPath: string | null | undefined,
    importDocuments: Map<string, ImportDocumentSnapshot> | undefined,
    namespace?: string,
): string | null {
    if (namespace) {
        return importDocuments?.get(namespace)?.resolvedPath ?? null;
    }
    return documentPath ?? null;
}

function assetPathFromUrl(
    url: string,
    documentPath: string | null | undefined,
    importDocuments?: Map<string, ImportDocumentSnapshot>,
): string | null {
    const decodedUrl = decodeUrlPath(url);
    const match =
        url.match(ASSET_REF_FULL_RE) ??
        decodedUrl.match(ASSET_REF_FULL_RE) ??
        stripUrlSuffix(decodedUrl).match(ASSET_REF_FULL_RE);
    if (!match) return null;
    const assetRef = parseAssetRefPath(match[1]);
    if (!assetRef) return null;
    const sourceDocumentPath = resolveAssetRefDocumentPath(
        documentPath,
        importDocuments,
        assetRef.namespace,
    );
    return sourceDocumentPath
        ? resolveDocumentAssetPath(sourceDocumentPath, assetRef.relativePath) ?? null
        : null;
}

export function parseMarkdownLinkFragment(
    url: string | undefined,
): MarkdownLinkFragment | null {
    const trimmed = url?.trim() ?? "";
    if (!trimmed) return null;
    const rawFragment = trimmed.startsWith("#")
        ? trimmed.slice(1)
        : extractUrlFragment(trimmed);
    if (!rawFragment) return null;

    const decoded = decodeUrlPath(rawFragment).trim();
    if (!decoded) return null;

    const lineMatch = decoded.match(/^(?:L|l|line[=:-])(\d+)(?:[-:](?:L|l)?\d+)?$/);
    if (lineMatch) {
        const line = Number(lineMatch[1]);
        if (Number.isInteger(line) && line > 0) {
            return { kind: "line", raw: decoded, heading: null, line };
        }
    }

    return { kind: "heading", raw: decoded, heading: decoded, line: null };
}

export function resolveMarkdownLocalPath(
    url: string | undefined,
    documentPath: string | null | undefined,
    importDocuments?: Map<string, ImportDocumentSnapshot>,
): string | null {
    if (!url) return null;
    const trimmed = url.trim();
    if (!trimmed || trimmed.startsWith("#") || trimmed.startsWith("//")) return null;

    const decodedTrimmed = decodeUrlPath(trimmed);
    const assetMatch =
        trimmed.match(ASSET_REF_FULL_RE) ??
        decodedTrimmed.match(ASSET_REF_FULL_RE) ??
        stripUrlSuffix(decodedTrimmed).match(ASSET_REF_FULL_RE);
    if (assetMatch) {
        const assetRef = parseAssetRefPath(assetMatch[1]);
        if (!assetRef) return null;
        const sourceDocumentPath = resolveAssetRefDocumentPath(
            documentPath,
            importDocuments,
            assetRef.namespace,
        );
        return sourceDocumentPath
            ? resolveDocumentAssetPath(sourceDocumentPath, assetRef.relativePath) ?? null
            : null;
    }

    const filePath = fileUrlToPath(trimmed);
    if (filePath) return normalizeSlashPath(filePath);

    if (URI_SCHEME_RE.test(trimmed)) return null;

    const rawPath = decodeUrlPath(stripUrlSuffix(trimmed));
    if (!rawPath) return null;
    if (isAbsoluteLocalPath(rawPath)) return normalizeSlashPath(rawPath);

    if (!documentPath) return null;
    const documentDir = dirNameFromPath(documentPath);
    if (!documentDir) return null;
    return normalizeSlashPath(`${documentDir}/${rawPath}`);
}

function normalizeHeadingText(value: string): string {
    return value
        .replace(/\s+/g, " ")
        .trim()
        .toLocaleLowerCase();
}

function markdownHeadingSlug(value: string): string {
    return normalizeHeadingText(value)
        .replace(/[^\p{L}\p{N}\s-]/gu, "")
        .replace(/\s+/g, "-");
}

function stripMarkdownInlineMarkup(value: string): string {
    return value
        .replace(/`([^`]+)`/g, "$1")
        .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1")
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        .replace(/<\/?[^>]+>/g, "")
        .replace(/[*_~]/g, "")
        .trim();
}

export function findMarkdownHeadingLine(
    markdown: string | undefined,
    heading: string | null | undefined,
): number | null {
    if (!markdown || !heading) return null;
    const expected = normalizeHeadingText(heading);
    const expectedSlug = markdownHeadingSlug(heading);
    const lines = markdown.split(/\r?\n/);
    for (let index = 0; index < lines.length; index += 1) {
        const match = lines[index]?.match(/^ {0,3}#{1,6}[ \t]+(.+?)\s*#*\s*$/);
        if (!match) continue;
        const label = stripMarkdownInlineMarkup(match[1] ?? "");
        if (
            normalizeHeadingText(label) === expected ||
            markdownHeadingSlug(label) === expectedSlug
        ) {
            return index + 1;
        }
    }
    return null;
}

function markdownLinkKindFromPath(filePath: string, source: MarkdownLinkSource): MarkdownLinkKind {
    const fileKind = getFileKind(filePath);
    if (source === "asset" && fileKind === "plain") return "asset";
    switch (fileKind) {
        case "markdown":
            return "markdown";
        case "json":
            return "json";
        case "html":
            return "html";
        case "image":
        case "svg":
            return "image";
        case "pdf":
            return "pdf";
        case "audio":
            return "audio";
        case "video":
            return "video";
        case "csv":
            return "csv";
        case "code":
            return "code";
        case "plain":
        default:
            return "file";
    }
}

export function classifyMarkdownLinkTarget(
    url: string | undefined,
    documentPath: string | null | undefined,
    importDocuments?: Map<string, ImportDocumentSnapshot>,
): MarkdownLinkTarget {
    const trimmed = url?.trim() ?? "";
    const fragment = parseMarkdownLinkFragment(trimmed);
    if (!trimmed) {
        return { kind: "file", source: "unknown", localPath: null, externalUrl: null, fragment };
    }
    if (trimmed.startsWith("#")) {
        return { kind: "anchor", source: "anchor", localPath: null, externalUrl: null, fragment };
    }
    if (trimmed.startsWith("//")) {
        return { kind: "web", source: "external", localPath: null, externalUrl: `https:${trimmed}`, fragment };
    }

    const assetPath = assetPathFromUrl(trimmed, documentPath, importDocuments);
    if (assetPath) {
        return {
            kind: markdownLinkKindFromPath(assetPath, "asset"),
            source: "asset",
            localPath: assetPath,
            externalUrl: null,
            fragment,
        };
    }

    const filePath = resolveMarkdownLocalPath(trimmed, documentPath, importDocuments);
    if (filePath) {
        return {
            kind: markdownLinkKindFromPath(filePath, "local"),
            source: "local",
            localPath: filePath,
            externalUrl: null,
            fragment,
        };
    }

    const externalUrl = externalUrlFromMarkdown(trimmed);
    if (externalUrl) {
        return { kind: "web", source: "external", localPath: null, externalUrl, fragment: null };
    }

    return { kind: "file", source: "unknown", localPath: null, externalUrl: null, fragment };
}

export function resolveAssetRefUrl(
    url: string | undefined,
    documentPath: string | null | undefined,
    importDocuments?: Map<string, ImportDocumentSnapshot>,
): string | undefined {
    if (!url) return url;
    const refUrl = decodeUrlPath(url);
    const refBase = stripUrlSuffix(refUrl);
    const match = refBase.match(ASSET_REF_FULL_RE);
    if (!match) return url;
    const assetRef = parseAssetRefPath(match[1]);
    if (!assetRef) return url;
    const sourceDocumentPath = resolveAssetRefDocumentPath(
        documentPath,
        importDocuments,
        assetRef.namespace,
    );
    if (!sourceDocumentPath) return url;
    const filePath = resolveDocumentAssetPath(sourceDocumentPath, assetRef.relativePath);
    if (!filePath) return url;
    try {
        const assetUrl = getPlatformService().getAssetUrl?.(filePath);
        return assetUrl ? `${assetUrl}${getUrlSuffix(refUrl)}` : url;
    } catch {
        return url;
    }
}

export function resolveMarkdownResourceUrl(
    url: string | undefined,
    documentPath: string | null | undefined,
    importDocuments?: Map<string, ImportDocumentSnapshot>,
): string | undefined {
    if (!url) return url;
    const assetUrl = resolveAssetRefUrl(url, documentPath, importDocuments);
    if (assetUrl !== url) return assetUrl;

    const filePath = resolveMarkdownLocalPath(url, documentPath, importDocuments);
    if (!filePath) return url;
    try {
        return getPlatformService().getAssetUrl?.(filePath) ?? url;
    } catch {
        return url;
    }
}

export function assetRefUrlTransform(url: string): string {
    const decodedUrl = decodeUrlPath(url);
    if (
        ASSET_REF_FULL_RE.test(url) ||
        ASSET_REF_FULL_RE.test(decodedUrl) ||
        ASSET_REF_FULL_RE.test(stripUrlSuffix(decodedUrl))
    ) return url;
    if (fileUrlToPath(url)) return url;
    return defaultUrlTransform(url);
}

function externalUrlFromMarkdown(url: string | undefined): string | null {
    if (!url) return null;
    const trimmed = url.trim();
    if (!trimmed || trimmed.startsWith("#")) return null;
    if (trimmed.startsWith("//")) return `https:${trimmed}`;
    if (fileUrlToPath(trimmed)) return null;
    const safeUrl = defaultUrlTransform(trimmed);
    return safeUrl && URI_SCHEME_RE.test(safeUrl) ? safeUrl : null;
}

export function faviconUrlFromExternalUrl(url: string | null | undefined): string | null {
    if (!url) return null;
    try {
        const parsed = new URL(url);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
        return `${parsed.origin}/favicon.ico`;
    } catch {
        return null;
    }
}

function shouldHandleAnchorClick(event: MouseEvent<HTMLAnchorElement>): boolean {
    return !event.defaultPrevented
        && event.button === 0
        && !event.metaKey
        && !event.ctrlKey
        && !event.shiftKey
        && !event.altKey;
}

function headingElementMatchesFragment(
    element: Element,
    fragment: MarkdownLinkFragment,
): boolean {
    const raw = fragment.raw.trim();
    if (!raw) return false;
    if (element.id === raw || element.getAttribute("data-toc-id") === raw) return true;
    const text = element.textContent ?? "";
    return normalizeHeadingText(text) === normalizeHeadingText(raw)
        || markdownHeadingSlug(text) === markdownHeadingSlug(raw);
}

function flashElement(element: Element): void {
    if (!(element instanceof HTMLElement)) return;
    element.classList.remove("toc-flash");
    void element.offsetWidth;
    element.classList.add("toc-flash");
}

function scrollToHeadingFragment(
    anchor: HTMLAnchorElement,
    fragment: MarkdownLinkFragment | null,
): boolean {
    if (!fragment || fragment.kind !== "heading") return false;
    const root = anchor.closest(".preview-content") ?? document;
    const headings = Array.from(root.querySelectorAll("h1,h2,h3,h4,h5,h6"));
    const target = headings.find((heading) =>
        headingElementMatchesFragment(heading, fragment),
    );
    if (!target) return false;
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    flashElement(target);
    return true;
}

function isSameLocalPath(
    left: string | null | undefined,
    right: string | null | undefined,
): boolean {
    return Boolean(left && right && normalizeSlashPath(left) === normalizeSlashPath(right));
}

const MARKDOWN_LINK_ICONS: Record<MarkdownLinkKind, LucideIcon> = {
    anchor: Hash,
    web: Globe2,
    asset: FileArchive,
    markdown: FileText,
    json: FileJson2,
    html: Globe2,
    image: FileImage,
    pdf: FileText,
    audio: FileAudio,
    video: FileVideo,
    csv: FileSpreadsheet,
    code: FileCode2,
    file: File,
};

const invalidAssetRefStyle = {
    font: "inherit",
    letterSpacing: "inherit",
    lineHeight: "inherit",
    overflowWrap: "break-word",
    wordBreak: "normal",
    color: "var(--content-inline-ref-warning-foreground)",
    cursor: "help",
    textDecorationLine: "underline",
    textDecorationStyle: "dotted",
    textDecorationThickness: "0.08em",
    textDecorationColor: "currentColor",
    textUnderlineOffset: "0.16em",
} satisfies CSSProperties;

function hasMarkdownClassName(
    className: string | string[] | undefined,
    expected: string,
): boolean {
    if (Array.isArray(className)) return className.includes(expected);
    return className?.split(/\s+/).includes(expected) ?? false;
}

function MarkdownLink({
    href,
    children,
    documentPath,
    importDocuments,
    options,
    className,
    title,
    ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & {
    documentPath: string | null | undefined;
    importDocuments?: Map<string, ImportDocumentSnapshot>;
    options: MarkdownInteractionOptions;
}) {
    const classNameText = Array.isArray(className)
        ? className.filter(Boolean).join(" ")
        : className;
    const isInvalidAssetRef = hasMarkdownClassName(
        classNameText,
        "magic-md-asset-ref-invalid",
    );
    const classifiedTarget = classifyMarkdownLinkTarget(href, documentPath, importDocuments);
    const target = hasMarkdownClassName(
        classNameText,
        "magic-md-asset-ref-link",
    )
        ? {
            ...classifiedTarget,
            kind: classifiedTarget.kind === "file" ? "asset" : classifiedTarget.kind,
            source: "asset" as const,
        }
        : classifiedTarget;
    const resolvedHref = resolveMarkdownResourceUrl(href, documentPath, importDocuments);
    const Icon = MARKDOWN_LINK_ICONS[target.kind] ?? Link;
    const faviconUrl = target.kind === "web"
        ? faviconUrlFromExternalUrl(target.externalUrl)
        : null;
    const [faviconFailed, setFaviconFailed] = useState(false);
    const resolvedTitle = title
        ?? target.localPath
        ?? target.externalUrl
        ?? href;

    if (isInvalidAssetRef) {
        return (
            <span
                {...(props as HTMLAttributes<HTMLSpanElement>)}
                className={["magic-md-inline-ref-warning", classNameText].filter(Boolean).join(" ")}
                data-md-ref-kind="asset"
                data-md-ref-status="warning"
                data-md-ref-message={resolvedTitle}
                style={invalidAssetRefStyle}
                title={resolvedTitle}
                aria-label={resolvedTitle}
            >
                <span
                    aria-hidden="true"
                    style={{
                        color: "var(--status-warning)",
                        marginInlineEnd: "0.25em",
                    }}
                >
                    ⚠
                </span>
                <span>{children}</span>
            </span>
        );
    }

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
        props.onClick?.(event);
        if (!shouldHandleAnchorClick(event)) return;

        if (target.source === "anchor" && target.fragment) {
            event.preventDefault();
            scrollToHeadingFragment(event.currentTarget, target.fragment);
            return;
        }

        if (
            target.localPath &&
            isSameLocalPath(target.localPath, documentPath) &&
            scrollToHeadingFragment(event.currentTarget, target.fragment)
        ) {
            event.preventDefault();
            return;
        }

        if (target.localPath) {
            event.preventDefault();
            if (options.openFile) {
                void options.openFile(
                    target.localPath,
                    { fragment: target.fragment, sourceUrl: href },
                );
            } else {
                void options.openExternal?.(target.localPath);
            }
            return;
        }

        if (target.externalUrl && options.openExternal) {
            event.preventDefault();
            void options.openExternal(target.externalUrl);
        }
    };

    return (
        <a
            {...props}
            className={["magic-md-markdown-link", classNameText].filter(Boolean).join(" ")}
            data-md-link-kind={target.kind}
            data-md-link-source={target.source}
            href={resolvedHref}
            onClick={handleClick}
            title={resolvedTitle}
        >
            {faviconUrl && !faviconFailed ? (
                <img
                    aria-hidden="true"
                    alt=""
                    className="magic-md-markdown-link-favicon"
                    src={faviconUrl}
                    onError={() => setFaviconFailed(true)}
                />
            ) : (
                <Icon aria-hidden="true" className="magic-md-markdown-link-icon" />
            )}
            <span className="magic-md-markdown-link-label">{children}</span>
        </a>
    );
}

function svgDataUrlFromContent(content: string): string {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(content)}`;
}

function MarkdownImage({
    src,
    alt,
    documentPath,
    importDocuments,
    ...props
}: ImgHTMLAttributes<HTMLImageElement> & {
    documentPath: string | null | undefined;
    importDocuments?: Map<string, ImportDocumentSnapshot>;
}) {
    const target = classifyMarkdownLinkTarget(src, documentPath, importDocuments);
    const resolvedSrc = resolveMarkdownResourceUrl(src, documentPath, importDocuments);
    const [fallbackSrc, setFallbackSrc] = useState<string | undefined>(undefined);

    useEffect(() => {
        setFallbackSrc(undefined);
        if (!target.localPath) return;
        if (getFileKind(target.localPath) !== "svg") return;
        if (resolvedSrc && resolvedSrc !== src) return;

        let cancelled = false;
        let engine: ReturnType<typeof getPlatformService>["engine"];
        try {
            engine = getPlatformService().engine;
        } catch {
            return;
        }

        void engine.workspaceReadFile({ path: target.localPath })
            .then((result) => {
                if (!cancelled) {
                    setFallbackSrc(svgDataUrlFromContent(result.content));
                }
            })
            .catch(() => {
                if (!cancelled) setFallbackSrc(undefined);
            });

        return () => {
            cancelled = true;
        };
    }, [resolvedSrc, src, target.localPath]);

    return (
        <img
            {...props}
            src={fallbackSrc ?? resolvedSrc}
            alt={alt ?? ""}
        />
    );
}

function textFromReactNode(node: ReactNode): string {
    if (node === null || node === undefined || typeof node === "boolean") return "";
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(textFromReactNode).join("");
    if (isValidElement<{ children?: ReactNode }>(node)) {
        return textFromReactNode(node.props.children);
    }
    return "";
}

async function writeClipboardText(text: string): Promise<boolean> {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {
            // Fall through to the DOM fallback below.
        }
    }

    if (typeof document === "undefined" || typeof document.execCommand !== "function") {
        return false;
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    textarea.style.opacity = "0";

    try {
        document.body.appendChild(textarea);
        textarea.select();
        return document.execCommand("copy");
    } catch {
        return false;
    } finally {
        textarea.remove();
    }
}

function MarkdownCodeBlock({
    children,
    className,
    node: _node,
    ...props
}: ComponentPropsWithoutRef<"pre"> & { node?: unknown }) {
    const t = useT("editor");
    const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [copyState, setCopyState] = useState<"idle" | "success" | "error">("idle");
    const codeText = useMemo(() => textFromReactNode(children), [children]);

    useEffect(
        () => () => {
            if (resetTimerRef.current) {
                clearTimeout(resetTimerRef.current);
            }
        },
        [],
    );

    const markCopyState = (state: "success" | "error") => {
        setCopyState(state);
        if (resetTimerRef.current) {
            clearTimeout(resetTimerRef.current);
        }
        resetTimerRef.current = setTimeout(() => setCopyState("idle"), 1200);
    };

    const handleCopy = async () => {
        const ok = await writeClipboardText(codeText);
        markCopyState(ok ? "success" : "error");
    };

    const title = copyState === "success"
        ? t("assetRefs.copySuccess")
        : copyState === "error"
            ? t("assetRefs.copyFailed")
            : t("assetRefs.copyCode");

    return (
        <div className="magic-md-code-block">
            <button
                type="button"
                className={[
                    "magic-md-code-copy-button",
                    copyState === "success" ? "is-success" : "",
                    copyState === "error" ? "is-error" : "",
                ].filter(Boolean).join(" ")}
                onClick={(event) => {
                    event.stopPropagation();
                    void handleCopy();
                }}
                title={title}
                aria-label={t("assetRefs.copyCode")}
            >
                {copyState === "success" ? (
                    <Check aria-hidden="true" size={13} />
                ) : copyState === "error" ? (
                    <XCircle aria-hidden="true" size={13} />
                ) : (
                    <Copy aria-hidden="true" size={13} />
                )}
            </button>
            <pre
                {...props}
                className={["magic-md-code-block-pre", className].filter(Boolean).join(" ")}
            >
                {children}
            </pre>
        </div>
    );
}

export function withAssetRefMarkdownComponents(
    documentPath: string | null | undefined,
    components: Components = {},
    options: MarkdownInteractionOptions = {},
    importDocuments?: Map<string, ImportDocumentSnapshot>,
): Components {
    return {
        ...components,
        a: ({ href, children, node: _node, ...props }) => {
            return (
                <MarkdownLink
                    {...props}
                    href={href}
                    documentPath={documentPath}
                    importDocuments={importDocuments}
                    options={options}
                >
                    {children}
                </MarkdownLink>
            );
        },
        img: ({ src, alt, node: _node, ...props }) => (
            <MarkdownImage
                {...props}
                src={src}
                alt={alt ?? ""}
                documentPath={documentPath}
                importDocuments={importDocuments}
            />
        ),
        pre: ({ node, ...props }) => (
            <MarkdownCodeBlock {...props} node={node} />
        ),
    };
}

function assetRefLabel(assetPath: string): string {
    return assetPath;
}

function createAssetLinkNode(
    rawRef: string,
    refPath: string,
    documentPath: string | null | undefined,
    importDocuments?: Map<string, ImportDocumentSnapshot>,
): MdastNode {
    const assetRef = parseAssetRefPath(refPath);
    if (!assetRef) {
        const title = translate("editor", "assetRefs.unsafePath", { ref: rawRef });
        return {
            type: "link",
            url: rawRef,
            title,
            data: {
                hProperties: {
                    className: ["magic-md-asset-ref-link", "magic-md-asset-ref-invalid"],
                    title,
                },
            },
            children: [{ type: "text", value: refPath }],
        };
    }

    const assetPath = assetPathFromUrl(rawRef, documentPath, importDocuments);
    if (!assetPath) {
        return { type: "text", value: rawRef };
    }
    const label = assetRef.namespace
        ? `${assetRef.namespace}/assets/${assetRef.relativePath}`
        : assetRef.relativePath;
    return {
        type: "link",
        url: rawRef,
        title: label,
        data: {
            hProperties: {
                className: ["magic-md-asset-ref-link"],
                title: label,
            },
        },
        children: [{ type: "text", value: assetRefLabel(label) }],
    };
}

function splitAssetRefText(
    value: string,
    documentPath: string | null | undefined,
    importDocuments?: Map<string, ImportDocumentSnapshot>,
): MdastNode[] {
    const nodes: MdastNode[] = [];
    let lastIndex = 0;
    ASSET_REF_RE.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = ASSET_REF_RE.exec(value)) !== null) {
        const [rawRef, refPath] = match;
        if (match.index > lastIndex) {
            nodes.push({ type: "text", value: value.slice(lastIndex, match.index) });
        }
        nodes.push(createAssetLinkNode(rawRef, refPath, documentPath, importDocuments));
        lastIndex = match.index + rawRef.length;
    }
    if (lastIndex < value.length) {
        nodes.push({ type: "text", value: value.slice(lastIndex) });
    }
    return nodes;
}

function transformAssetRefTextNodes(
    node: MdastNode,
    documentPath: string | null | undefined,
    importDocuments?: Map<string, ImportDocumentSnapshot>,
): void {
    if (!node.children || node.children.length === 0) return;
    if (node.type === "link" || node.type === "image") return;

    const nextChildren: MdastNode[] = [];
    let changed = false;
    for (const child of node.children) {
        if (child.type === "text" && typeof child.value === "string") {
            const splitNodes = splitAssetRefText(child.value, documentPath, importDocuments);
            nextChildren.push(...splitNodes);
            changed = changed || splitNodes.length !== 1 || splitNodes[0] !== child;
            continue;
        }
        transformAssetRefTextNodes(child, documentPath, importDocuments);
        nextChildren.push(child);
    }
    if (changed) {
        node.children = nextChildren;
    }
}

export function createAssetRefRemarkPlugin(
    documentPath: string | null | undefined,
    importDocuments?: Map<string, ImportDocumentSnapshot>,
) {
    return function assetRefRemarkPlugin() {
        return function transform(tree: MdastNode) {
            transformAssetRefTextNodes(tree, documentPath, importDocuments);
        };
    };
}
